import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PenNib, Warning, User, UserCircle } from "@phosphor-icons/react";

/**
 * Dropdown de seleção do Assinante do Documento.
 * Lista todos os assinantes disponíveis para o município (manuais + usuários do sistema).
 *
 * Props:
 *  - municipioId: string (obrigatório) — filtro por município
 *  - value: string — id do assinante selecionado (pode ser "user:XXX" ou uuid manual)
 *  - onChange: (id) => void
 *  - testid: string — testid do select
 *  - required: bool — mostra asterisco e valida
 *  - compact: bool — compacto (sem label)
 */
export default function SignatarioPicker({ municipioId, value, onChange, testid = "signatario-picker", required = true, compact = false }) {
  const [items, setItems] = useState(null);  // null = loading, [] = vazio, [...] = lista

  useEffect(() => {
    if (!municipioId) { setItems([]); return; }
    let cancelled = false;
    setItems(null);
    api.get(`/signatarios?municipio_id=${municipioId}`)
      .then((r) => { if (!cancelled) setItems(r.data?.items || []); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [municipioId]);

  if (!municipioId) {
    return (
      <div className="text-xs text-[#94A3B8] italic">Selecione um município para escolher o assinante.</div>
    );
  }
  if (items === null) {
    return <div className="text-xs text-[#94A3B8]">Carregando assinantes…</div>;
  }
  if (items.length === 0) {
    return (
      <div className="rounded-md bg-[#EF4444]/10 border border-[#EF4444]/40 p-3.5 flex items-start gap-2.5 text-sm">
        <Warning size={18} weight="fill" className="text-[#EF4444] flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="text-[#FCA5A5] font-medium mb-0.5">Nenhum assinante cadastrado para este município</div>
          <div className="text-xs text-[#94A3B8]">
            Você precisa cadastrar ao menos um assinante antes de gerar o documento.
            <Link to="/signatarios" className="ml-1 text-[#F59E0B] hover:text-[#FCD34D] underline">
              Cadastrar assinante →
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const selected = items.find((s) => s.id === value);
  return (
    <div>
      {!compact && (
        <label className="uppercase-label block mb-2 flex items-center gap-1.5">
          <PenNib size={12} weight="duotone" className="text-[#F59E0B]" />
          Assinante do documento {required && <span className="text-[#EF4444]">*</span>}
        </label>
      )}
      <select
        data-testid={testid}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="tce-input w-full px-3 py-2.5 rounded-md text-sm"
        required={required}
      >
        <option value="">Selecione o assinante…</option>
        {items.map((s) => (
          <option key={s.id} value={s.id}>
            {`${s.nome} — ${s.cargo}${s.source === "user" ? " (usuário)" : ""}`}
          </option>
        ))}
      </select>
      {selected && (
        <div className="mt-2 flex items-center gap-2 text-xs text-[#94A3B8]">
          {selected.source === "user" ? <UserCircle size={14} weight="duotone" className="text-[#8B5CF6]" /> : <User size={14} weight="duotone" className="text-[#F59E0B]" />}
          <span>
            Será assinado por <strong className="text-[#E9EEF5]">{selected.nome}</strong> ({selected.cargo})
            {selected.cpf && ` · CPF ${selected.cpf}`}
          </span>
        </div>
      )}
    </div>
  );
}
